// Sprint 44 XL - Motion design (S46: theme transition).
// Duraciones, easings y helpers de inyeccion CSS. Sin libs externas.
// Los componentes XL llaman ensureKeyframes() + injectCSS(id, rules)
// dentro de su useEffect para garantizar idempotencia.
//
// S46: anadida la regla html.xl-theme-changing para que el toggle de
// tema haga fade global de 120ms en el arbol XL. La clase la pone
// useXLTheme en tokens.js solo durante ~130ms, asi que no afecta las
// transitions normales (hover/focus/scan) el resto del tiempo.

export const duration = {
  fast:    100,
  base:    150,
  slow:    220,
  shimmer: 1400,
  theme:   120, // S46 - duracion del fade del toggle de tema.
};

export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  accel:    'cubic-bezier(0.4, 0, 1, 1)',
  decel:    'cubic-bezier(0, 0, 0.2, 1)',
};

const KEYFRAMES_ID = 'xl-motion-keyframes';

const KEYFRAMES_CSS = `
@keyframes xl-fade-in {
  from { opacity: 0 }
  to   { opacity: 1 }
}
@keyframes xl-fade-out {
  from { opacity: 1 }
  to   { opacity: 0 }
}
@keyframes xl-slide-up {
  from { opacity: 0; transform: translateY(6px) }
  to   { opacity: 1; transform: translateY(0) }
}
@keyframes xl-slide-down {
  from { opacity: 0; transform: translateY(-6px) }
  to   { opacity: 1; transform: translateY(0) }
}
@keyframes xl-shimmer {
  0%   { background-position: -400px 0 }
  100% { background-position:  400px 0 }
}

.xl-motion-fade-in    { animation: xl-fade-in    150ms cubic-bezier(0,0,0.2,1) both }
.xl-motion-slide-up   { animation: xl-slide-up   150ms cubic-bezier(0,0,0.2,1) both }
.xl-motion-slide-down { animation: xl-slide-down 150ms cubic-bezier(0,0,0.2,1) both }
.xl-motion-hover      { transition: all 120ms cubic-bezier(0,0,0.2,1) }

.xl-shimmer {
  background: linear-gradient(
    90deg,
    var(--xl-color-surface)     0%,
    var(--xl-color-surface-alt) 50%,
    var(--xl-color-surface)     100%
  );
  background-size: 800px 100%;
  animation: xl-shimmer 1400ms linear infinite;
}

.xl-focus-ring:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}

/* S46 - Theme transition.
   Solo activa durante el toggle (clase temporal puesta por useXLTheme,
   ~130ms). Cubre las 3 propiedades que cambian de paleta:
   background-color, color, border-color. !important garantiza que
   pisa transitions locales solo en esa ventana corta. */
html.xl-theme-changing,
html.xl-theme-changing * {
  transition:
    background-color 120ms cubic-bezier(0, 0, 0.2, 1),
    color            120ms cubic-bezier(0, 0, 0.2, 1),
    border-color     120ms cubic-bezier(0, 0, 0.2, 1) !important;
}

@media (prefers-reduced-motion: reduce) {
  .xl-motion-fade-in, .xl-motion-slide-up, .xl-motion-slide-down,
  .xl-shimmer { animation: none !important; }
  .xl-motion-hover { transition: none !important; }
  /* S46 - respetar reduce-motion tambien en el fade del tema. */
  html.xl-theme-changing,
  html.xl-theme-changing * {
    transition: none !important;
  }
}
`;

/**
 * Inserta los keyframes globales una sola vez. Idempotente.
 */
export function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.appendChild(document.createTextNode(KEYFRAMES_CSS));
  document.head.appendChild(style);
}

/**
 * Inyecta CSS scoped con id unico. Idempotente: si ya existe, no hace nada.
 * Los componentes XL lo usan para registrar sus reglas propias.
 */
export function injectCSS(id, css) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}
